import { useEffect, useMemo, useRef, useState } from 'react';
import {
  askUserInputSchema,
  questionsOfCall,
  type AskUserQuestion,
  type QuestionDraft,
} from '@inference.engine/agentic/human-input';
import type { PendingApproval, ToolApprovalDecision } from '../api/pending-approvals.api';
import { usePendingApprovals } from './usePendingApprovals';
import { useStreamedPendingApprovals, type StreamedPause } from './useStreamedPendingApprovals';

export interface PauseDecisions {
  /** Everything the paused turn waits on. */
  approvals: PendingApproval[];
  /** The ones answered with a yes or no. */
  toolApprovals: PendingApproval[];
  /** The ones answered with answers (the ask_user tool). */
  questions: PausedQuestion[];
  decided: Partial<Record<string, ToolApprovalDecision>>;
  /** What the turn still waits on, questions first; nothing once every decision is made. */
  waitingOn: 'questions' | 'approvals' | undefined;
  /**
   * Whether a new message must wait. Questions do not hold it: sending something else is how a
   * person moves on from them, and the server tells the agent they went unanswered. A tool
   * approval does, since a message is no answer to whether something should happen.
   */
  blocksMessages: boolean;
  /** Whether a tool approval is still undecided, which is what a held message waits on until it is. */
  approvalOpen: boolean;
  /** The answers being chosen for one question card, one draft per question. */
  draftsOf: (approvalId: string) => readonly (QuestionDraft | undefined)[];
  setDraft: (approvalId: string, index: number, draft: QuestionDraft) => void;
  decide: (decision: ToolApprovalDecision) => void;
  sending: boolean;
  failed: boolean;
}

/** A paused question call with the questions its input asks, read once. */
export interface PausedQuestion {
  approval: PendingApproval;
  questions: AskUserQuestion[];
}

/**
 * The questions the input can draw, and everything else. A question it cannot read stays an
 * ordinary approval, so it can still be denied instead of blocking the composer with nothing on
 * screen.
 */
function partition(approvals: readonly PendingApproval[]) {
  const questions: PausedQuestion[] = [];
  const toolApprovals: PendingApproval[] = [];
  for (const approval of approvals) {
    // A tool can ask about its own call; an ask_user call's questions are its input.
    const asked =
      approval.kind === 'question'
        ? (askUserInputSchema.safeParse({ questions: approval.questions }).data?.questions ??
          questionsOfCall(approval.toolName, approval.input))
        : undefined;
    if (asked) questions.push({ approval, questions: asked });
    else toolApprovals.push(approval);
  }
  return { questions, toolApprovals };
}

const NO_DRAFTS: readonly (QuestionDraft | undefined)[] = [];

/**
 * The decisions a paused turn waits on, across the approval card in the thread and the questions
 * in the input. A turn resumes once, so nothing is sent until every one of them is decided, and
 * then all of them go together.
 *
 * Drafts live here rather than in the cards: sending empties the list until it is read again, which
 * unmounts the cards, and a send that failed brings the same pause back to be sent again.
 */
export function usePauseDecisions({
  contextId,
  busy,
  onAnswer,
  streamed,
}: {
  contextId: string | null;
  busy: boolean;
  onAnswer: (decisions: ToolApprovalDecision[]) => unknown;
  /** Reads the pause off these messages instead, for a conversation its host does not keep. */
  streamed?: StreamedPause;
}): PauseDecisions {
  const stored = usePendingApprovals(streamed ? null : contextId, busy);
  const fromStream = useStreamedPendingApprovals(streamed, busy);
  const { approvals, clear, refetch } = streamed ? fromStream : stored;
  const [decided, setDecided] = useState<Partial<Record<string, ToolApprovalDecision>>>({});
  const [drafts, setDrafts] = useState<Partial<Record<string, (QuestionDraft | undefined)[]>>>({});
  const [sending, setSending] = useState(false);
  const [failedFor, setFailedFor] = useState<string>();

  // The list is emptied while a send is out and read again after it, so the pause it comes back
  // with is the same one unless its approvals differ. Only a different one starts over.
  const pauseKey = approvals.map((approval) => approval.approvalId).join(',');
  const lastPause = useRef(pauseKey);
  useEffect(() => {
    setDecided({});
    if (!pauseKey || pauseKey === lastPause.current) return;
    lastPause.current = pauseKey;
    setDrafts({});
  }, [pauseKey]);

  const decide = (decision: ToolApprovalDecision) => {
    const next = { ...decided, [decision.approvalId]: decision };
    setDecided(next);
    const decisions = approvals.map((approval) => next[approval.approvalId]);
    if (decisions.some((d) => d === undefined)) return;
    setSending(true);
    setFailedFor(undefined);
    // Answered is answered: left cached, the prompts would show again for the moment between the
    // resumed turn finishing and the list being read again.
    clear();
    void Promise.resolve(onAnswer(decisions as ToolApprovalDecision[]))
      .catch(() => {
        setDecided({});
        setFailedFor(pauseKey);
        void refetch();
      })
      .finally(() => {
        setSending(false);
      });
  };

  const { questions, toolApprovals } = useMemo(() => partition(approvals), [approvals]);
  const undecided = (approval: PendingApproval) => decided[approval.approvalId] === undefined;
  /* eslint-disable i18next/no-literal-string -- state names, not copy */
  const waitingOn = questions.some(({ approval }) => undecided(approval))
    ? 'questions'
    : toolApprovals.some(undecided)
      ? 'approvals'
      : undefined;
  /* eslint-enable i18next/no-literal-string */

  return {
    approvals,
    toolApprovals,
    questions,
    decided,
    waitingOn,
    blocksMessages: toolApprovals.length > 0,
    approvalOpen: toolApprovals.some(undecided),
    draftsOf: (approvalId) => drafts[approvalId] ?? NO_DRAFTS,
    setDraft: (approvalId, index, draft) => {
      setDrafts((current) => {
        const next = [...(current[approvalId] ?? [])];
        next[index] = draft;
        return { ...current, [approvalId]: next };
      });
    },
    decide,
    sending,
    failed: failedFor !== undefined && failedFor === pauseKey,
  };
}
